import { useCallback, useEffect, useEffectEvent } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanCard } from '@/components';
import { EmailTimingSection } from './EmailTimingSection';
import { EmailActionsMenu } from './EmailActionsMenu';
import { PublishSuccessModal } from './PublishSuccessModal';
import { EmailFormActions } from './EmailFormActions';
import { useEmailFormHandlers } from '../../emails/hooks';
import { emailFormSchema } from '../validations/emailForm';
import type { EmailFormData } from '../../emails/types/email.types';
import { useEmailEditorStore } from '../stores';
import { useModal } from '@/hooks/useModal';
import { useDirtyState } from '@/hooks/useDirtyState';
import { useSaveEmail } from '../queries';
import { createIconElement, FlowStatus, stanNotify } from '@/lib';
import { useStanRouter } from '@/hooks/useStanRouter';
import { EmailSubjectField } from '../../components/EmailSubjectField';
import { EmailBodyField } from '../../components/EmailBodyField';
import AlertCircle from '@/assets/icons/ic-alert-circle.svg';

interface EmailEditorFormProps {
    flowId: number;
    emailId?: number;
}

export const EmailEditorForm = ({ flowId, emailId }: EmailEditorFormProps) => {
    const publishSuccessModal = useModal();
    const { push } = useStanRouter();

    // Get data from store (populated in EmailEditorLayout)
    const defaultValues = useEmailEditorStore(state => state.defaultValues);
    const isFirstTimeBasedEmail = useEmailEditorStore(state => state.isFirstTimeBasedEmail);
    const isCartAbandonedFlow = useEmailEditorStore(state => state.isCartAbandonedFlow);
    const triggerEventName = useEmailEditorStore(state => state.triggerEventName);
    const actionsDisabled = useEmailEditorStore(state => state.actionsDisabled);
    const trigger = useEmailEditorStore(state => state.trigger);
    const canAddAnotherEmail = useEmailEditorStore(state => state.canAddAnotherEmail);
    const templateVariables = useEmailEditorStore(state => state.templateVariables);
    const templateDefaults = useEmailEditorStore(state => state.templateDefaults);
    const setSubject = useEmailEditorStore(state => state.setSubject);
    const setBody = useEmailEditorStore(state => state.setBody);

    // Mutations
    const { mutateAsync: saveEmailMutation, isPending: isSaving } = useSaveEmail(flowId, emailId);

    // Create form instance
    const formMethods = useForm<EmailFormData>({
        resolver: zodResolver(emailFormSchema),
        mode: 'onChange',
        defaultValues: defaultValues || {
            subject: '',
            body: '',
            delay: '1',
            delayUnit: 'hours',
        },
    });

    const {
        control,
        formState: { errors, isDirty },
        setValue,
        reset,
    } = formMethods;

    // Register dirty state with global navigation system
    useDirtyState(isDirty, `email-editor-${flowId}-${emailId || 'new'}`);

    const resetForm = useEffectEvent((values: EmailFormData) => {
        reset(values);
    });

    // Reset form when default values change
    useEffect(() => {
        if (defaultValues) {
            resetForm(defaultValues);
        }
    }, [defaultValues]);

    // Mutation handlers
    const handleSave = useCallback(
        async (data: EmailFormData, status: FlowStatus) => {
            try {
                const savedEmail = await saveEmailMutation({ data, status });

                // Reset form to clear dirty state after successful save
                reset(data, { keepValues: true });

                if (status === 'draft') {
                    stanNotify({
                        title: 'Email saved as draft',
                        type: 'success',
                    });

                    // If this was a new email (no emailId), redirect to the edit page with the new ID
                    if (!emailId && savedEmail?.flow_email_id) {
                        push(`/flows/${flowId}/emails/${savedEmail.flow_email_id}`, { skipDirtyCheck: true });
                    }
                }

                if (status === 'published') {
                    publishSuccessModal.open();
                }
            } catch (error) {
                const message = (error as any).response?.data?.message || 'Failed to save email';
                stanNotify({
                    title: message,
                    type: 'error',
                });
                throw error;
            }
        },
        [saveEmailMutation, publishSuccessModal, reset, emailId, flowId, push]
    );

    // Use form handlers hook
    const { handleSaveAsDraft, handlePublish } = useEmailFormHandlers({
        formMethods,
        onSave: handleSave,
        isFirstTimeBasedEmail,
    });

    return (
        <>
            <StanCard className='p-6'>
                {!!emailId && !(actionsDisabled && trigger === 'time') && (
                    <EmailActionsMenu emailId={emailId} flowId={flowId} />
                )}

                {actionsDisabled && trigger === 'time' && (
                    <div className='border-warning-intense bg-warning-dimmed mb-6 flex w-full justify-between gap-1 rounded-md border px-4 py-2 max-md:flex-col md:items-center md:gap-2'>
                        <div className='flex gap-1.5 md:items-center'>
                            {createIconElement(AlertCircle, { className: 'size-4.5 shrink-0 text-warning-bold' })}
                            <p className='text-body-xs md:text-body-sm font-medium'>
                                This email has already been sent and cannot be edited.
                            </p>
                        </div>
                        {emailId && (
                            <StanButton
                                name='email-view-recipients'
                                variant='link'
                                size='xs'
                                onClick={() => push(`/flows/recipients/${emailId}`)}
                                className='w-fit shrink-0 max-md:self-end'
                            >
                                View Recipients
                            </StanButton>
                        )}
                    </div>
                )}

                <FormProvider {...formMethods}>
                    <form className='w-full space-y-6 max-md:py-2'>
                        {/* Email Timing Section */}
                        {!isFirstTimeBasedEmail && (
                            <Controller
                                name='delay'
                                control={control}
                                render={({ field: delayField }) => (
                                    <Controller
                                        name='delayUnit'
                                        control={control}
                                        render={({ field: unitField }) => (
                                            <EmailTimingSection
                                                delay={delayField.value}
                                                delayUnit={unitField.value}
                                                isCartAbandonedFlow={isCartAbandonedFlow}
                                                triggerEventName={triggerEventName}
                                                disabled={actionsDisabled}
                                                onChange={(newDelay, newUnit) => {
                                                    delayField.onChange(newDelay);
                                                    unitField.onChange(newUnit);
                                                }}
                                                error={errors.delay?.message || errors.delayUnit?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        )}

                        {/* Subject Input */}
                        <EmailSubjectField
                            control={control}
                            errors={errors}
                            disabled={actionsDisabled}
                            templateVariables={templateVariables}
                            onSubjectChange={setSubject}
                        />

                        {/* Body Input */}
                        <EmailBodyField
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            disabled={actionsDisabled}
                            templateVariables={templateVariables}
                            templateDefaults={templateDefaults}
                            onBodyChange={setBody}
                        />

                        {/* Action Buttons */}
                        <EmailFormActions
                            onSaveAsDraft={handleSaveAsDraft}
                            onPublish={handlePublish}
                            disabled={actionsDisabled}
                            isSaving={isSaving}
                        />
                    </form>
                </FormProvider>
            </StanCard>

            <PublishSuccessModal
                isOpen={publishSuccessModal.isOpen}
                onClose={() => publishSuccessModal.close()}
                flowId={flowId}
                trigger={trigger}
                canAddAnotherEmail={canAddAnotherEmail}
            />
        </>
    );
};
